/**
 * CLE-2811 (2026-09-28) — Ani: "Payer 87 — instead should be Medical Mutual of Ohio SuperMed. Instead of id
 * show the name."
 *
 * Every place the run page names a payer or a provider — the payer groups, the recommendation cards, the
 * Lines of Business picker, the save panel, the previews while a pass runs, the progress strip and the
 * conversation — resolves it here, in one order:
 *
 *   1. the run's own `payerNames` (the server's name for every payer id the run references), merged with
 *      the slim progress poll's (`payerNames` for the plans a pass in flight has found);
 *   2. the payers list the page already loads;
 *   3. the name the agent wrote next to the id, when it is a real name and not "Payer 87";
 *   4. "Unknown payer" — never the id itself.
 *
 * The id stays reachable as a tooltip (`payerIdTitle`) for a super admin quoting it in a bug report; the
 * tool is super-admin only.
 *
 * Pure, so the order and the "never an id" rule are tested without a DOM.
 */

import { UNKNOWN_PAYER, UNKNOWN_PROVIDER } from "./copy";

/** A payer id (as a string) → its name. `IDraftRunDetail.payerNames` / `IDraftRunProgress.payerNames`. */
export type PayerNameMap = Record<string, string>;

/** A "name" that is really an id: "87", "Payer 87", "payer id 87", "#87". */
export const isIdLikeName = (name: string): boolean =>
  /^\s*(?:payer[\s_-]*(?:id)?\s*)?[#:=]?\s*\d+\s*$/i.test(name);

/** A string worth showing as a name: non-empty, and not an id in disguise. */
const usableName = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" && !isIdLikeName(v)
    ? v.trim()
    : null;

const keyOf = (id: unknown): string | null =>
  id === null ||
  id === undefined ||
  (typeof id === "string" && id.trim() === "")
    ? null
    : String(id);

/** Merge name maps; later maps win (the slim poll is fresher than the run it refetches less often). */
export const mergePayerNames = (
  ...maps: (PayerNameMap | null | undefined)[]
): PayerNameMap =>
  maps.reduce<PayerNameMap>(
    (acc, m) =>
      m && typeof m === "object"
        ? {
            ...acc,
            ...Object.fromEntries(
              Object.entries(m).filter(([, v]) => usableName(v) !== null)
            ),
          }
        : acc,
    {}
  );

export interface IPayerNameSources {
  /** The run's (and the slim progress's) `payerNames`. */
  runNames?: PayerNameMap | null;
  /** The payers list (`GET /payers`), for an id the run did not name. */
  payers?: ReadonlyArray<{ id: number | string; name: string }> | null;
}

/** Resolves a payer id to what the reviewer reads. `fallbackName` is the agent's own `payerName`. */
export type PayerNameResolver = (
  id: unknown,
  fallbackName?: string | null
) => string;

/** Build the resolver once per render: the payers list becomes a map, not a scan per lookup. */
export const payerNameResolver = (
  sources: IPayerNameSources
): PayerNameResolver => {
  const listed = new Map<string, string>();
  (sources.payers ?? []).forEach((p) => {
    const name = usableName(p?.name);
    if (name) listed.set(String(p.id), name);
  });
  const runNames = sources.runNames ?? {};
  return (id, fallbackName) => {
    const key = keyOf(id);
    if (key !== null) {
      const fromRun = usableName(runNames[key]);
      if (fromRun) return fromRun;
      const fromList = listed.get(key);
      if (fromList) return fromList;
    }
    return usableName(fallbackName) ?? UNKNOWN_PAYER;
  };
};

/** One-off convenience over `payerNameResolver`. */
export const payerDisplayName = (
  id: unknown,
  sources: IPayerNameSources,
  fallbackName?: string | null
): string => payerNameResolver(sources)(id, fallbackName);

/**
 * Payer groups (`groupByPayer`) with every payer group's `payerName` resolved — what the payer headings,
 * the Lines of Business picker and the checklist print. The contract-wide group has no payer and is left.
 */
export const namePayerGroups = <
  G extends { payerId: number | null; payerName: string | null }
>(
  groups: G[],
  resolve: PayerNameResolver
): G[] =>
  groups.map((g) =>
    g.payerId != null ? { ...g, payerName: resolve(g.payerId, g.payerName) } : g
  );

/**
 * The same rule for providers (the "Same for provider ids" half of Ani's note): the run's own provider,
 * then the providers list, then the agent's name, then "Unknown provider". A provider id is a uuid, which
 * says even less than a payer id.
 */
export const providerNameResolver = (sources: {
  runProvider?: { id: string | null; name: string | null } | null;
  providers?: ReadonlyArray<{ id: string; name: string }> | null;
}): PayerNameResolver => {
  const known = new Map<string, string>();
  (sources.providers ?? []).forEach((p) => {
    const name = usableName(p?.name);
    if (name) known.set(String(p.id), name);
  });
  const own = sources.runProvider;
  const ownName = usableName(own?.name);
  if (own?.id && ownName) known.set(own.id, ownName);
  return (id, fallbackName) => {
    const key = keyOf(id);
    const hit = key !== null ? known.get(key) : undefined;
    return hit ?? usableName(fallbackName) ?? UNKNOWN_PROVIDER;
  };
};

// ---------------------------------------------------------------------------------------------
// Free text: the conversation and the progress strip
// ---------------------------------------------------------------------------------------------

const escapeRe = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * "payer 87", "Payer ID 87", "payerId: 87", "payer_id=87", "payer #87".
 *
 * Must stay aligned with `PAYER_ID_IN_TEXT` in `agents/scripts/contract-facts-pipeline.mjs` — the worker's
 * own ids-to-names pass over the same text — including its unit guard (`NOT_A_UNIT` below). This one also
 * accepts "payer_id" / "payer-id".
 */
const PAYER_REF = String.raw`\bpayer(?:[\s_-]*id)?\s*[#:=]?\s*`;

/**
 * Put right after the number: "reach the payer 90 days after DOS", "the payer 30-day notice", "payer 5% of
 * charges" are a quantity, not payer 90 / 30 / 5 — even when the run has a payer with that id.
 */
const NOT_A_UNIT = String.raw`(?!\s*(?:-\s*)?(?:days?|months?|years?|weeks?|%|percent|calendar|business))`;

/**
 * The AI's own words — its replies, its streamed narration, its step-by-step activity — with every
 * reference to a payer the run knows by id replaced by that payer's name:
 *
 * - "Medical Mutual of Ohio SuperMed (87)" / "(payer 87)" / "(id 87)" → "Medical Mutual of Ohio SuperMed"
 * - "payer 87 (Medical Mutual of Ohio SuperMed)" → "Medical Mutual of Ohio SuperMed"
 * - "payer 87", "Payer ID 87", "payerId: 87" → "Medical Mutual of Ohio SuperMed"
 *
 * Only ids in `names` are touched, and only in those shapes: a bare "(87)" after some other word could be
 * a day count, so it is left alone. Her own messages are never passed through this.
 */
export const humanizePayerRefs = (
  text: string,
  names: PayerNameMap | null | undefined
): string => {
  if (!text || !names) return text;
  const entries = Object.entries(names).filter(
    ([id, name]) => /^\d+$/.test(id) && usableName(name) !== null
  );
  if (entries.length === 0) return text;
  let out = text;
  entries.forEach(([id, rawName]) => {
    const name = rawName.trim();
    const n = escapeRe(name);
    out = out
      .replace(
        new RegExp(
          `${n}\\s*\\((?:payer\\s*)?(?:id\\s*)?[#:]?\\s*${id}${NOT_A_UNIT}\\)`,
          "gi"
        ),
        // A function, not the string: a "$" in a payer's name must not read as a replacement pattern.
        () => name
      )
      .replace(
        new RegExp(
          `${PAYER_REF}${id}\\b${NOT_A_UNIT}\\s*\\(\\s*${n}\\s*\\)`,
          "gi"
        ),
        () => name
      );
  });
  const byId = new Map(entries.map(([id, name]) => [id, name.trim()]));
  return out.replace(
    new RegExp(`${PAYER_REF}(\\d+)\\b${NOT_A_UNIT}`, "gi"),
    (match, id: string) => byId.get(id) ?? match
  );
};

/** The parts of a run the AI wrote: its replies, and the live progress's narration and activity. */
interface IHumanizableRun {
  progress: {
    narration?: string | null;
    activity?: string[] | null;
  } | null;
  messages: { role: string; body: string }[];
}

/**
 * The run as the conversation and the progress strip show it: the AI's replies, narration and activity
 * with payer ids replaced by names (`humanizePayerRefs`). Her own messages are left exactly as she typed
 * them. Returns the run itself when there is nothing to resolve.
 */
export const humanizeRunPayerRefs = <R extends IHumanizableRun>(
  run: R,
  names: PayerNameMap | null | undefined
): R => {
  if (!names || Object.keys(names).length === 0) return run;
  const h = (t: string) => humanizePayerRefs(t, names);
  const { progress } = run;
  return {
    ...run,
    progress: progress
      ? {
          ...progress,
          ...(typeof progress.narration === "string"
            ? { narration: h(progress.narration) }
            : {}),
          ...(Array.isArray(progress.activity)
            ? {
                activity: progress.activity.map((a) =>
                  typeof a === "string" ? h(a) : a
                ),
              }
            : {}),
        }
      : progress,
    messages: (run.messages ?? []).map((m) =>
      m.role === "assistant" ? { ...m, body: h(m.body) } : m
    ),
  };
};
